import React from 'react';
import { Milestone, Evidence } from '../../services/api';
import { formatEther } from '../../services/web3';
import DisputeDetailSection from './DisputeDetailSection';

interface MilestoneCardProps {
  milestone: Milestone;
  milestoneId: number;
  isClient: boolean;
  isProvider: boolean;
  isArbitrator?: boolean;
  currentUser: string;
  onApprove: () => void;
  onRelease: () => void;
  onOpenDispute?: () => void;
  onResolveDispute?: () => void;
  onSubmitEvidence?: () => void;
  loading?: boolean;
  depositRequired?: boolean;
  documentRequired?: boolean;
  documents?: any[];
  onUploadDocument?: (milestoneIndex: number, file: File, comment: string) => Promise<void>;
  uploadingDocument?: boolean;
  disputeReason?: string;
  disputeInitiator?: string;
  evidence?: Evidence[];
  clientAddress?: string;
  providerAddress?: string;
  arbitratorAddress?: string | null;
}

const MilestoneCard: React.FC<MilestoneCardProps> = ({
  milestone,
  milestoneId,
  isClient,
  isProvider,
  isArbitrator = false,
  currentUser,
  onApprove,
  onRelease,
  onOpenDispute,
  onResolveDispute,
  onSubmitEvidence,
  loading = false,
  depositRequired = false,
  documentRequired = false,
  documents = [],
  onUploadDocument,
  uploadingDocument = false,
  disputeReason,
  disputeInitiator,
  evidence = [],
  clientAddress,
  providerAddress,
  arbitratorAddress,
}) => {
  const [showUpload, setShowUpload] = React.useState(false);
  const [uploadFile, setUploadFile] = React.useState<File | null>(null);
  const [uploadComment, setUploadComment] = React.useState('');
  const amount = formatEther(milestone.amount);

  // Check dispute status
  const disputeStatus = milestone.disputeStatus || 'none';
  const hasActiveDispute = disputeStatus === 'open' || disputeStatus === 'under_review';
  const isDisputed = disputeStatus !== 'none';

  const canApprove = (isClient && !milestone.approvedByClient) || (isProvider && !milestone.approvedByProvider);
  const canRelease = milestone.approvedByClient && milestone.approvedByProvider && !milestone.released;
  const isBlocked = depositRequired || documentRequired || hasActiveDispute;
  const canOpenDispute = (isClient || isProvider) && !milestone.released && !isDisputed && !!arbitratorAddress;

  return (
    <div className="bg-white border border-[#471b001e] rounded-xl p-5">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h4 className="text-base font-semibold text-[#471b00] font-poly mb-1">
            Milestone {milestoneId + 1}
          </h4>
          <p className="text-sm text-[#471b007f] font-dm">
            {milestone.description}
          </p>
        </div>
        <span className="text-lg font-semibold text-[#471b00] font-poly">
          {parseFloat(amount).toFixed(4)} MATIC
        </span>
      </div>

      <div className="space-y-2 mb-4">
        {/* Dispute Status Badge */}
        {isDisputed && (
          <div className="flex items-center gap-2 mb-2">
            {disputeStatus === 'open' && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Dispute Open
              </span>
            )}
            {disputeStatus === 'under_review' && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
                Under Review
              </span>
            )}
            {disputeStatus === 'resolved' && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                Dispute Resolved
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 text-xs">
          <div className={`w-2 h-2 rounded-full ${milestone.approvedByClient ? 'bg-green-500' : 'bg-gray-300'}`}></div>
          <span className="text-[#471b007f] font-dm">
            Client approval: {milestone.approvedByClient ? 'Approved' : 'Pending'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <div className={`w-2 h-2 rounded-full ${milestone.approvedByProvider ? 'bg-green-500' : 'bg-gray-300'}`}></div>
          <span className="text-[#471b007f] font-dm">
            Provider approval: {milestone.approvedByProvider ? 'Approved' : 'Pending'}
          </span>
        </div>
        {milestone.released && (
          <div className="flex items-center gap-2 text-xs">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span className="text-green-600 font-dm font-medium">Payment Released</span>
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {canApprove && (
          <button
            onClick={onApprove}
            disabled={loading || isBlocked}
            className="px-4 py-2 bg-gradient-to-r from-[#FF3333] to-[#FF5555] text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            title={
              isBlocked
                ? hasActiveDispute
                  ? 'Milestone has an active dispute'
                  : depositRequired
                  ? 'Deposit required'
                  : 'Document upload required'
                : ''
            }
          >
            {loading ? 'Processing...' : 'Approve'}
          </button>
        )}
        {canRelease && (
          <button
            onClick={onRelease}
            disabled={loading || isBlocked}
            className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            title={
              isBlocked
                ? hasActiveDispute
                  ? 'Milestone has an active dispute'
                  : depositRequired
                  ? 'Deposit required'
                  : 'Document upload required'
                : ''
            }
          >
            {loading ? 'Processing...' : 'Release Payment'}
          </button>
        )}
        {milestone.released && (
          <div className="px-4 py-2 bg-green-50 text-green-700 rounded-lg font-medium text-sm text-center">
            Released
          </div>
        )}
        {canOpenDispute && onOpenDispute && (
          <button
            onClick={onOpenDispute}
            disabled={loading}
            className="px-4 py-2 border border-red-500 text-red-600 rounded-lg font-medium hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Open Dispute
          </button>
        )}
        {(isClient || isProvider) && !milestone.released && !isDisputed && !arbitratorAddress && (
          <span className="text-xs text-[#471b007f] font-dm italic">
            Assign an arbitrator to enable disputes
          </span>
        )}
        {isArbitrator && hasActiveDispute && onResolveDispute && (
          <button
            onClick={onResolveDispute}
            disabled={loading}
            className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            Resolve Dispute
          </button>
        )}
      </div>

      {isDisputed && (
        <DisputeDetailSection
          disputeStatus={disputeStatus}
          disputeReason={disputeReason}
          disputeInitiator={disputeInitiator}
          evidence={evidence}
          canSubmitEvidence={(isClient || isProvider || isArbitrator) && !!onSubmitEvidence}
          onSubmitEvidence={onSubmitEvidence || (() => {})}
          clientAddress={clientAddress}
          providerAddress={providerAddress}
          arbitratorAddress={arbitratorAddress}
        />
      )}
    </div>
  );
};

export default MilestoneCard;

